import React from 'react';
import {
  Circle as KCircle,
  Rect as KRect,
  Line as KLine,
  Arrow as KArrow,
  Text as KText,
  Group,
  RegularPolygon as KRegularPolygon,
} from 'react-konva';

/**
 * Renders a single object from its percent-based props, scaled to stage size.
 * Coordinates in `props` are always 0..1 of stage (W, H).
 */
/**
 * Interpolate a numeric value between tIn and tOut.
 * progress = 0 at tIn, 1 at tOut, clamped.
 */
function progressAt(obj, currentTime) {
  const tIn = obj.tIn ?? 0;
  const tOut = obj.tOut ?? tIn + 1;
  const span = Math.max(0.0001, tOut - tIn);
  return Math.max(0, Math.min(1, (currentTime - tIn) / span));
}

function lerp(a, b, t) { return a + (b - a) * t; }

/**
 * Given a sorted list of keyframes [{t, props}], return interpolated props at currentTime.
 * - Before first keyframe → first keyframe's props
 * - After last keyframe → last keyframe's props
 * - Between → linear interpolation of numeric fields; non-numeric taken from prev
 */
export function interpolateKeyframes(keyframes, currentTime) {
  if (!keyframes || keyframes.length === 0) return null;
  if (keyframes.length === 1) return keyframes[0].props;
  if (currentTime <= keyframes[0].t) return keyframes[0].props;
  const last = keyframes[keyframes.length - 1];
  if (currentTime >= last.t) return last.props;

  let prev = keyframes[0];
  let next = keyframes[1];
  for (let i = 1; i < keyframes.length; i++) {
    if (keyframes[i].t >= currentTime) {
      prev = keyframes[i - 1];
      next = keyframes[i];
      break;
    }
  }
  const span = next.t - prev.t;
  const alpha = span > 0 ? (currentTime - prev.t) / span : 0;
  const out = {};
  const allKeys = new Set([...Object.keys(prev.props), ...Object.keys(next.props)]);
  for (const key of allKeys) {
    const pa = prev.props[key];
    const pb = next.props[key];
    if (typeof pa === 'number' && typeof pb === 'number') {
      out[key] = lerp(pa, pb, alpha);
    } else if (Array.isArray(pa) && Array.isArray(pb) && pa.length === pb.length) {
      out[key] = pa.map((v, i) => {
        const vb = pb[i];
        if (typeof v === 'number' && typeof vb === 'number') return lerp(v, vb, alpha);
        if (Array.isArray(v) && Array.isArray(vb) && v.length === vb.length) {
          return v.map((vi, j) =>
            typeof vi === 'number' && typeof vb[j] === 'number' ? lerp(vi, vb[j], alpha) : vi
          );
        }
        return v;
      });
    } else {
      out[key] = pa ?? pb;
    }
  }
  return out;
}

/**
 * Extract spatial/animatable props for a given shape type.
 * Returns null for types that don't support keyframe animation.
 */
export function extractSpatialProps(type, props) {
  switch (type) {
    case 'circle':
    case 'spotlight':
      return { x: props.x, y: props.y, r: props.r };
    case 'playerToken':
      return { x: props.x, y: props.y };
    case 'pentagon':
      return { x: props.x, y: props.y, r: props.r, rotation: props.rotation || 0 };
    case 'rect':
      return { x: props.x, y: props.y, w: props.w, h: props.h };
    case 'text':
      return { x: props.x, y: props.y };
    case 'line':
    case 'arrow':
      return { points: props.points };
    case 'curvedArrow':
      return { points: props.points, curve: props.curve ?? 0.25 };
    case 'polygon':
      return { points: props.points };
    case 'playerDuo':
    case 'playerMove':
    case 'playerDribble':
      return { points: props.points };
    default:
      return null;
  }
}

export function supportsKeyframes(type) {
  return extractSpatialProps(type, {}) !== null;
}

export default function ShapeRenderer({
  object,
  stageW,
  stageH,
  isSelected,
  onSelect,
  onChange,
  draggable,
  interactive = true,
  currentTime = 0,
}) {
  const { type, id } = object;
  // Merge keyframe-interpolated spatial props on top of static props
  const animated = interpolateKeyframes(object.keyframes, currentTime);
  const props = animated ? { ...object.props, ...animated } : object.props;
  const commonEvents = interactive ? {
    onMouseDown: (e) => {
      e.cancelBubble = true;
      onSelect?.(id);
    },
    onTap: (e) => {
      e.cancelBubble = true;
      onSelect?.(id);
    },
  } : { listening: false };

  const selectionStroke = isSelected ? { shadowColor: '#007AFF', shadowBlur: 8, shadowOpacity: 0.9 } : {};

  // Common drag handler — converts stage px back to percent (for x,y-based shapes)
  const makeDragEnd = (updater) => (e) => {
    const node = e.target;
    const x = node.x() / stageW;
    const y = node.y() / stageH;
    onChange?.(id, updater(x, y));
  };

  // Drag handler for point-based shapes (flat array of [x1,y1,x2,y2,...])
  // Translates all points by the drag delta and resets node to origin.
  const makePointsDragEndFlat = (currentPoints) => (e) => {
    const node = e.target;
    const dx = node.x();
    const dy = node.y();
    if (dx === 0 && dy === 0) return;
    node.position({ x: 0, y: 0 });
    const dxPct = dx / stageW;
    const dyPct = dy / stageH;
    const next = currentPoints.map((v, i) => (i % 2 === 0 ? v + dxPct : v + dyPct));
    onChange?.(id, { points: next });
  };

  // Drag handler for point-based shapes with nested points ([[x,y], ...])
  const makePointsDragEndNested = (currentPoints) => (e) => {
    const node = e.target;
    const dx = node.x();
    const dy = node.y();
    if (dx === 0 && dy === 0) return;
    node.position({ x: 0, y: 0 });
    const dxPct = dx / stageW;
    const dyPct = dy / stageH;
    const next = currentPoints.map(([x, y]) => [x + dxPct, y + dyPct]);
    onChange?.(id, { points: next });
  };

  switch (type) {
    case 'circle': {
      const cx = props.x * stageW;
      const cy = props.y * stageH;
      const r = props.r * Math.min(stageW, stageH);
      return (
        <KCircle
          x={cx}
          y={cy}
          radius={r}
          stroke={props.stroke}
          strokeWidth={props.strokeWidth}
          fill={props.fill || 'transparent'}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makeDragEnd((x, y) => ({ x, y }))}
        />
      );
    }

    case 'rect': {
      const x = props.x * stageW;
      const y = props.y * stageH;
      const w = props.w * stageW;
      const h = props.h * stageH;
      return (
        <KRect
          x={x}
          y={y}
          width={w}
          height={h}
          stroke={props.stroke}
          strokeWidth={props.strokeWidth}
          dash={props.dash || undefined}
          fill={props.fill || 'transparent'}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makeDragEnd((x, y) => ({ x, y }))}
        />
      );
    }

    case 'line': {
      const [x1, y1, x2, y2] = props.points;
      return (
        <KLine
          points={[x1 * stageW, y1 * stageH, x2 * stageW, y2 * stageH]}
          stroke={props.stroke}
          strokeWidth={props.strokeWidth}
          lineCap="round"
          hitStrokeWidth={Math.max(12, props.strokeWidth * 4)}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makePointsDragEndFlat(props.points)}
        />
      );
    }

    case 'arrow': {
      const [x1, y1, x2, y2] = props.points;
      return (
        <KArrow
          points={[x1 * stageW, y1 * stageH, x2 * stageW, y2 * stageH]}
          stroke={props.stroke}
          fill={props.stroke}
          strokeWidth={props.strokeWidth}
          pointerLength={Math.max(8, props.strokeWidth * 3)}
          pointerWidth={Math.max(8, props.strokeWidth * 3)}
          lineCap="round"
          hitStrokeWidth={Math.max(14, props.strokeWidth * 4)}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makePointsDragEndFlat(props.points)}
        />
      );
    }

    case 'text': {
      const x = props.x * stageW;
      const y = props.y * stageH;
      const fontSize = (props.fontSize || 0.035) * stageH;
      return (
        <KText
          x={x}
          y={y}
          text={props.text || 'Texte'}
          fontSize={fontSize}
          fontStyle="bold"
          fill={props.stroke}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makeDragEnd((x, y) => ({ x, y }))}
        />
      );
    }

    case 'curvedArrow': {
      const [ax, ay, bx, by] = props.points;
      const x1 = ax * stageW, y1 = ay * stageH;
      const x2 = bx * stageW, y2 = by * stageH;
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      const curveMag = (props.curve ?? 0.25) * len;
      const cpx = mx - (dy / len) * curveMag;
      const cpy = my + (dx / len) * curveMag;

      const progress = progressAt(object, currentTime);
      const steps = 24;
      const pts = [];
      const maxI = Math.max(2, Math.round(steps * progress));
      for (let i = 0; i <= maxI; i++) {
        const t = i / steps;
        const u = 1 - t;
        const x = u * u * x1 + 2 * u * t * cpx + t * t * x2;
        const y = u * u * y1 + 2 * u * t * cpy + t * t * y2;
        pts.push(x, y);
      }
      const showArrow = progress >= 0.9;
      const Component = showArrow ? KArrow : KLine;
      return (
        <Component
          points={pts}
          stroke={props.stroke}
          fill={props.stroke}
          strokeWidth={props.strokeWidth}
          pointerLength={Math.max(8, props.strokeWidth * 3)}
          pointerWidth={Math.max(8, props.strokeWidth * 3)}
          lineCap="round"
          lineJoin="round"
          hitStrokeWidth={Math.max(14, props.strokeWidth * 4)}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makePointsDragEndFlat(props.points)}
        />
      );
    }

    case 'pentagon': {
      const cx = props.x * stageW;
      const cy = props.y * stageH;
      const r = props.r * Math.min(stageW, stageH);
      return (
        <KRegularPolygon
          x={cx}
          y={cy}
          sides={props.sides || 5}
          radius={r}
          stroke={props.stroke}
          strokeWidth={props.strokeWidth}
          fill={props.fill || 'transparent'}
          rotation={props.rotation || 0}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makeDragEnd((x, y) => ({ x, y }))}
        />
      );
    }

    case 'polygon': {
      const ptsPct = props.points || [];
      const pts = ptsPct.flatMap(([x, y]) => [x * stageW, y * stageH]);
      return (
        <KLine
          points={pts}
          stroke={props.stroke}
          strokeWidth={props.strokeWidth}
          fill={props.fill || 'transparent'}
          closed
          lineCap="round"
          lineJoin="round"
          hitStrokeWidth={Math.max(14, props.strokeWidth * 4)}
          draggable={draggable}
          {...selectionStroke}
          {...commonEvents}
          onDragEnd={makePointsDragEndNested(ptsPct)}
        />
      );
    }

    case 'playerToken': {
      const cx = props.x * stageW;
      const cy = props.y * stageH;
      const r = (props.r || 0.02) * Math.min(stageW, stageH);
      return (
        <Group x={cx} y={cy} draggable={draggable} {...commonEvents}
          onDragEnd={(e) => {
            const n = e.target;
            onChange?.(id, { x: n.x() / stageW, y: n.y() / stageH });
          }}
        >
          <KCircle
            radius={r}
            fill={props.fill || '#FF3B30'}
            stroke="#FFFFFF"
            strokeWidth={2}
            {...selectionStroke}
          />
          {props.label ? (
            <KText
              x={-r}
              y={-r * 0.55}
              width={2 * r}
              align="center"
              text={String(props.label)}
              fontSize={r * 1.1}
              fontStyle="bold"
              fill="#FFFFFF"
            />
          ) : null}
        </Group>
      );
    }

    case 'playerDuo': {
      const [ax, ay, bx, by] = props.points;
      const x1 = ax * stageW, y1 = ay * stageH;
      const x2 = bx * stageW, y2 = by * stageH;
      const r = (props.r || 0.02) * Math.min(stageW, stageH);
      const fill = props.fill || '#FF3B30';
      return (
        <Group
          draggable={draggable}
          {...commonEvents}
          onDragEnd={makePointsDragEndFlat(props.points)}
        >
          <KLine
            points={[x1, y1, x2, y2]}
            stroke={fill}
            strokeWidth={Math.max(1, props.strokeWidth || 2)}
            dash={[6, 4]}
            listening={false}
          />
          <KCircle x={x1} y={y1} radius={r} fill={fill} stroke="#FFFFFF" strokeWidth={2} {...selectionStroke} />
          <KCircle x={x2} y={y2} radius={r} fill={fill} stroke="#FFFFFF" strokeWidth={2} />
        </Group>
      );
    }

    case 'playerMove':
    case 'playerDribble': {
      // Token at start + arrow (dashed = move, wavy solid = dribble) to end
      const [ax, ay, bx, by] = props.points;
      const x1 = ax * stageW, y1 = ay * stageH;
      const x2 = bx * stageW, y2 = by * stageH;
      const r = (props.r || 0.018) * Math.min(stageW, stageH);
      const fill = props.fill || '#FF3B30';
      const sw = props.strokeWidth || 3;

      let arrowPoints;
      let dash;
      if (type === 'playerDribble') {
        // Wavy arrow — sine wave perpendicular to chord
        const dx = x2 - x1, dy = y2 - y1;
        const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const ux = dx / len, uy = dy / len;
        const nx = -uy, ny = ux; // perpendicular
        const amp = Math.min(14, len * 0.08);
        const waves = Math.max(3, Math.floor(len / 28));
        const steps = waves * 8;
        arrowPoints = [];
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const bx0 = x1 + ux * (t * len);
          const by0 = y1 + uy * (t * len);
          const offset = Math.sin(t * Math.PI * 2 * waves) * amp;
          arrowPoints.push(bx0 + nx * offset, by0 + ny * offset);
        }
        dash = undefined;
      } else {
        arrowPoints = [x1, y1, x2, y2];
        dash = [8, 6];
      }

      return (
        <Group
          draggable={draggable}
          {...commonEvents}
          onDragEnd={makePointsDragEndFlat(props.points)}
        >
          <KArrow
            points={arrowPoints}
            stroke={fill}
            fill={fill}
            strokeWidth={sw}
            pointerLength={Math.max(10, sw * 3)}
            pointerWidth={Math.max(10, sw * 3)}
            dash={dash}
            lineCap="round"
            lineJoin="round"
            tension={type === 'playerDribble' ? 0 : 0}
            listening={false}
          />
          <KCircle x={x1} y={y1} radius={r} fill={fill} stroke="#FFFFFF" strokeWidth={2} {...selectionStroke} />
        </Group>
      );
    }

    case 'spotlight': {
      // Circular bright area with rest of stage dimmed.
      // Implemented as a full-stage dark rect with a transparent "hole".
      const cx = props.x * stageW;
      const cy = props.y * stageH;
      const r = props.r * Math.min(stageW, stageH);
      return (
        <Group {...commonEvents}>
          {/* Dimming mask using 4 rects around the hole (cheaper than compositing) */}
          <KRect x={0} y={0} width={stageW} height={cy - r} fill="rgba(0,0,0,0.55)" listening={false} />
          <KRect x={0} y={cy + r} width={stageW} height={stageH - (cy + r)} fill="rgba(0,0,0,0.55)" listening={false} />
          <KRect x={0} y={cy - r} width={cx - r} height={2 * r} fill="rgba(0,0,0,0.55)" listening={false} />
          <KRect x={cx + r} y={cy - r} width={stageW - (cx + r)} height={2 * r} fill="rgba(0,0,0,0.55)" listening={false} />
          <KCircle
            x={cx}
            y={cy}
            radius={r}
            stroke={props.stroke}
            strokeWidth={props.strokeWidth}
            draggable={draggable}
            {...selectionStroke}
            onDragEnd={makeDragEnd((x, y) => ({ x, y }))}
          />
        </Group>
      );
    }

    default:
      return null;
  }
}

// Factory helpers — create a default object for each tool at start point (px → %)
export function buildObjectFromDrag({ tool, start, end, stageW, stageH, color, strokeWidth }) {
  const id = `tmp`; // caller replaces with real id
  const sx = start.x / stageW;
  const sy = start.y / stageH;
  const ex = end.x / stageW;
  const ey = end.y / stageH;

  const base = { id, tIn: null, tOut: null };

  switch (tool) {
    case 'circle': {
      const dx = ex - sx;
      const dy = ey - sy;
      const r = Math.sqrt(dx * dx + dy * dy);
      return { ...base, type: 'circle', props: { x: sx, y: sy, r, stroke: color, strokeWidth, fill: null } };
    }
    case 'rect': {
      const x = Math.min(sx, ex);
      const y = Math.min(sy, ey);
      const w = Math.abs(ex - sx);
      const h = Math.abs(ey - sy);
      return { ...base, type: 'rect', props: { x, y, w, h, stroke: color, strokeWidth, dash: [10, 6] } };
    }
    case 'line':
      return { ...base, type: 'line', props: { points: [sx, sy, ex, ey], stroke: color, strokeWidth } };
    case 'arrow':
      return { ...base, type: 'arrow', props: { points: [sx, sy, ex, ey], stroke: color, strokeWidth } };
    case 'curvedArrow':
      return { ...base, type: 'curvedArrow', props: { points: [sx, sy, ex, ey], curve: 0.25, stroke: color, strokeWidth } };
    case 'pentagon': {
      const dx = ex - sx;
      const dy = ey - sy;
      const r = Math.max(0.03, Math.sqrt(dx * dx + dy * dy));
      return { ...base, type: 'pentagon', props: { x: sx, y: sy, r, sides: 5, rotation: 0, stroke: color, strokeWidth, fill: null } };
    }
    case 'spotlight': {
      const dx = ex - sx;
      const dy = ey - sy;
      const r = Math.max(0.03, Math.sqrt(dx * dx + dy * dy));
      return { ...base, type: 'spotlight', props: { x: sx, y: sy, r, stroke: color, strokeWidth } };
    }
    case 'text':
      return { ...base, type: 'text', props: { x: sx, y: sy, text: 'Double-clic pour éditer', fontSize: 0.035, stroke: color, strokeWidth } };
    case 'playerToken':
      return { ...base, type: 'playerToken', props: { x: sx, y: sy, r: 0.022, fill: '#FF3B30' } };
    case 'playerDuo':
      return { ...base, type: 'playerDuo', props: { points: [sx, sy, ex, ey], r: 0.022, fill: '#FF3B30', strokeWidth: 2 } };
    case 'playerMove':
      return { ...base, type: 'playerMove', props: { points: [sx, sy, ex, ey], r: 0.02, fill: '#FF3B30', strokeWidth } };
    case 'playerDribble':
      return { ...base, type: 'playerDribble', props: { points: [sx, sy, ex, ey], r: 0.02, fill: '#FF3B30', strokeWidth } };
    default:
      return null;
  }
}
